import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Button,
  Box,
  Typography,
  styled,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getActivePolls, submitPollResponse } from '../apiCalls/feedback/feedback';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#FFE8BC',
    borderRadius: 16,
    padding: theme.spacing(2),
    minWidth: 300,
    minHeight: 300,
    border: 'black 2px solid',
    overflow: 'visible',
    [theme.breakpoints.down('sm')]: {
      minWidth: '95%',
      margin: '10px',
      padding: theme.spacing(1),
    }
  }
}));

const HeaderButton = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFA800',
  color: 'black',
  padding: '8px 16px',
  borderRadius: 24,
  fontWeight: 600,
  border: 'black 2px solid',
  position: 'absolute',
  top: '-20px',
  left: '50%',
  transform: 'translateX(-50%)',
  maxWidth: 230,
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '0.9rem',
    padding: '6px 12px',
    whiteSpace: 'nowrap',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FAAE1C',
  color: 'black',
  borderRadius: 16,
  padding: '10px 20px',
  '&:hover': {
    backgroundColor: '#e69e1a',
  },
  '&.Mui-disabled': {
    backgroundColor: '#FAAE1C',
    color: 'black',
    opacity: 0.6
  },
  fontWeight: 600,
  border: '1px solid black',
  textTransform: 'none'
}));

const OptionBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  border: '2px solid black',
  borderRadius: 8,
  margin: '8px 0',
  padding: '12px',
  cursor: 'pointer',
  transition: 'transform 0.2s, backgroundColor 0.2s',
  '&:hover': {
    transform: 'scale(1.02)',
    backgroundColor: '#f5f5f5'
  },
  '&.selected': {
    backgroundColor: '#e3f2fd',
    borderColor: '#1976d2'
  }
}));

const PollDialog = ({ open, onClose }) => {
  const [polls, setPolls] = useState([]);
  const [currentPoll, setCurrentPoll] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchPolls();
    }
  }, [open]);

  const fetchPolls = async () => {
    try {
      setLoading(true);
      const response = await getActivePolls();
      if (response.data.success && response.data.polls.length > 0) {
        const availablePolls = response.data.polls.filter(poll => !poll.has_submitted);
        if (availablePolls.length > 0) {
          setCurrentPoll(availablePolls[0]);
        }
      }
      
      // DUMMY DATA
      // const dummyPoll = {
      //   id: "dummy_poll_123",
      //   question: "What type of content you want to watch",
      //   options: {
      //     "1": "Science",
      //     "2": "Technology", 
      //     "3": "World",
      //     "4": "Gaming",
      //     "5": null
      //   },
      //   has_submitted: false
      // };
      // setCurrentPoll(dummyPoll);
    } catch (error) {
      console.error('Error fetching polls:', error);
      setError('Failed to load poll');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedOption || !currentPoll) return;

    try {
      setSubmitting(true);
      setError('');
      
      const response = await submitPollResponse({
        poll_id: currentPoll.id,
        selected_option: selectedOption
      });
      
      if (response.data.success) {
        setError(response.data.message);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to submit response');
      }
      
    } catch (error) {
      console.error('Error submitting poll:', error);
      setError('Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOptionClick = (optionKey) => {
    setSelectedOption(optionKey);
  };

  const getValidOptions = () => {
    if (!currentPoll?.options) return [];
    return Object.entries(currentPoll.options).filter(([key, value]) => value !== null);
  };

  if (!open) return null;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box>
        <HeaderButton>
          Your Opinion Matters!
        </HeaderButton>
        <IconButton
          onClick={onClose}
          sx={{
            '&:hover': { backgroundColor: '#dbc69f' },
            position: 'absolute',
            top: '-20px',
            right: '-10px',
            border: '2px solid black',
            color: 'black',
            backgroundColor: '#FFE8BC',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ p: 3, mt: 4 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : currentPoll ? (
          <>
            <Typography
              variant="h6"
              align="center"
              sx={{ 
                mb: 3, 
                color: 'black',
                fontWeight: 600
              }}
            >
              {currentPoll.question}
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              {getValidOptions().map(([optionKey, optionValue]) => (
                <OptionBox
                  key={optionKey}
                  onClick={() => handleOptionClick(optionKey)}
                  className={selectedOption === optionKey ? 'selected' : ''}
                >
                  <Typography
                    variant="body1"
                    sx={{ 
                      color: 'black', 
                      fontWeight: selectedOption === optionKey ? 600 : 400,
                      textAlign: 'center'
                    }}
                  >
                    {optionValue}
                  </Typography>
                </OptionBox>
              ))}
            </Box>

            {error && (
              <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>
                {error}
              </Typography>
            )}

            <Box display="flex" justifyContent="center" gap={2}>
              {/* <StyledButton onClick={onClose} disabled={submitting}>
                Skip
              </StyledButton> */}
              <StyledButton 
                onClick={handleSubmit} 
                disabled={!selectedOption || submitting}
                variant="contained"
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
              </StyledButton>
            </Box>
          </>
        ) : null}
      </DialogContent>
    </StyledDialog>
  );
};

export default PollDialog;